import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { format, parseISO } from 'date-fns';
import {
  Col,
  Container,
  Row,
  Card,
  CardHeader,
  Modal,
  ModalBody
} from 'reactstrap';
import Carousel from '../Carousel';
import { getImagePath } from '../../utils';
import { formatProductsByType, onSelectAddOnCarouseHandler } from '../../utils/products'
import { translate } from '../Translate';
import Button from '../Button';
import ModalDtv from '../Modals';
import PurchaseModal from '../../containers/PurchaseModal';
import Summary from '../Summary';
import { RulesName, RulesType } from '../../enums';
import { languageDates } from '../../utils/validations';


const ChangeAddOn = ({
  history,
  userPlan,
  intl,
  user,
  client,
  upgradeAddOnSubscriptions,
  downgradeAddOnSubscriptions,
  methodId,
  addOnSubscriptions,
  setAddOnsToShow,
  addOnsToShow,
  products,
  addOnList,
  productsToBuy,
  addProduct,
  subscriptions,
  applyProductRules
}) => {
  const alertSVG = getImagePath('ic_alert.svg', client);
  const approvedSVG = getImagePath('ic_approved.svg', client);
  const rejectedSVG = getImagePath('ic_rejected.svg', client);
  const querystring = window.location.search;
  const params = new URLSearchParams(querystring)
  const language = params.get('language');
  const addOnToDowngrade = params.get('downgradeAddOn');
  const formattedAddOnsToCarousel = formatProductsByType(addOnsToShow || addOnList);

  const [modalPurchase, setModalPurchase] = useState(null);
  const [modalAddExtra, setModalAddExtra] = useState(false);

  useEffect(() => {
    applyProductRules({
      ruleType: RulesType.ADDITIONAL_PURCHASE,
      ruleName: RulesName.DOWNGRADEABLE_TO,
      productId: addOnToDowngrade
    });
  }, []);

  const downgradeAddOn = async () => {
    const cancelAddOns = [addOnToDowngrade];
    const purchaseAddOns = productsToBuy?.map(pkg => pkg.productID);
    try {
      const { status: statusDowngrade } = await downgradeAddOnSubscriptions({
        cancelAddOns, token: user.token, addOnSubscriptions, methodId, products
      });
      const { status: statusUpgrade, body } = await upgradeAddOnSubscriptions({
        purchaseAddOns, addOnSubscriptions, products, token: user.token, methodId
      });
      const status = statusUpgrade === 200 && statusDowngrade === 200 ? 200 : 400;
      if (statusUpgrade === 200 && statusDowngrade === 200) {
        setModalPurchase({
          code: status,
          transaction: body?.transaction,
          img: status >= 300 ? alertSVG : approvedSVG,
          default: status >= 300 ? 'error' : 'default',
          handleClick: () => {
            setModalPurchase(null)
            if (status <= 300) {
              history.goBack()
            }
          },
          handleCloseModal: () => { history.goBack() }
        })
      } else {
        setModalPurchase({
          code: 300,
          img: rejectedSVG,
          default: 'error',
          handleClick: () => {
            setModalPurchase(false)
          },
          handleCloseModal: () => { setModalPurchase(false) }
        })
      }
    } catch (err) {
      setModalPurchase({
        code: 300,
        img: alertSVG,
        default: 'error',
        handleClick: () => {
          setModalPurchase(null)
        },
        handleCloseModal: () => { setModalPurchase(null) }
      })
    };
  }

  return (
    <Container className="tbx-addOn-plus">
      <Row>
        <Col>
          <h1 className="myAccount__subscription_title">
            <FormattedMessage
              id="MyAccount.title"
            />
          </h1>
        </Col>
      </Row>
      <Row>
        <Col>
          <h2 className="tbx-addOn-plus__title">
            <FormattedMessage id="MyAccount.subscription.addOnPlus.title" />
          </h2>
        </Col>
      </Row>
      <Row>
        <Col style={{ flex: '0 0 72%', maxWidth: '72%' }}>
          {Object.entries(formattedAddOnsToCarousel).map(([addOnType, addOnsCarousel]) => {
            return (
              <Card className="tbx-card-carousel" key={addOnType}>
                <CardHeader className="tbx-card-header">
                  <h4 className="tbx-card-carousel__title">
                    <FormattedMessage id={addOnType} />
                  </h4>
                </CardHeader>
                <Carousel
                  intl={intl}
                  onSelect={(addOn) => {
                    onSelectAddOnCarouseHandler(addOn, (addOnsToShow || addOnList), setAddOnsToShow, addProduct)
                  }}
                  addOnSubscriptions={subscriptions?.addOns}
                  exchangeRate={user.exchangeRate}
                  products={addOnsCarousel}
                />
              </Card>
            )
          })}
        </Col>
        <div className="summary-content left">
          <Summary
            selectedProducts={productsToBuy || []}
            handleContinueButton={() => setModalAddExtra(true)}
            subscriptions={subscriptions?.addOns || []}
            user={user}
          />
        </div>
      </Row>
      <ModalDtv
        title={translate(intl, 'SubscriptionManagement.addExtra.title')}
        isOpenModal={modalAddExtra}
        onToggleModal={() => { setModalAddExtra(false) }}
        description={
          <FormattedMessage
            id="SubscriptionManagement.addExtra.body"
            values={{
              date: format(parseISO(userPlan?.expiresAt),
              languageDates.includes(language.toLowerCase()) ? 'dd/MM/yy' : 'LL/dd/yyyy')
            }}
          />
        }
        primaryButton={{
          text: translate(intl, 'SubscriptionManagement.addExtraCancel'),
          onClick: () => { setModalAddExtra(false) }
        }}
        secondaryButton={{
          text: translate(intl, 'SubscriptionManagement.addExtra'),
          onClick: () => {
            setModalAddExtra(false)
            downgradeAddOn()
          }
        }}
      />
      {modalPurchase && (
        <PurchaseModal
          isOpenModal={Boolean(modalPurchase)}
          onToggleModal={false}
          code={modalPurchase?.code}
          transaction={modalPurchase?.transaction}
          image={modalPurchase?.img && (
            <img
              className="PaymentStatus__img"
              src={modalPurchase?.img}
              alt="modal payment status"
            />
          )}
          title={
            <FormattedMessage
              id={`PaymentStatus.subscription.messages.${modalPurchase?.code}.title`}
              defaultMessage={translate(intl, `PaymentStatus.subscription.messages.${modalPurchase?.default}.title`)}
            />
          }
          description={
            <FormattedMessage
              id={`PaymentStatus.subscription.messages.${modalPurchase?.code}.body`}
              defaultMessage={translate(intl, `PaymentStatus.subscription.messages.${modalPurchase?.default}.body`)}
            />
          }
          errorDescription={
            <FormattedMessage
              id="PaymentStatus.subscription.messages.default.status"
              defaultMessage={translate(intl, `PaymentStatus.subscription.buttons.${modalPurchase?.default}.submit`)}
              values={{ code: modalPurchase?.code || 'unknown' }}
            />
          }
          activateButton={{
            isNeutral: false,
            text: ({
              unlinked: translate(intl, 'PaymentStatus.subscription.buttons.default.submit.unlinked'),
              linked: translate(intl, 'PaymentStatus.subscription.buttons.default.submit.linked')
            })
          }}
          secondaryButton={{
            text: (
              <FormattedMessage
                id={`PaymentStatus.subscription.buttons.${modalPurchase?.code}.submit`}
                defaultMessage={translate(intl, `PaymentStatus.subscription.buttons.${modalPurchase?.default}.submit`)}
              />
            ),
            onClick: () => { modalPurchase.handleClick() }
          }}
        />
      )}
      <Modal
        className="crm-modal"
        centered
        isOpen={addOnList.length === 0}
      >
        <ModalBody>
          <div className="PaymentStatus">
            <div className="PaymentStatus__main">
              <p style={{ color: 'white', fontSize: '18px' }}>
                <FormattedMessage
                  id="MyAccount.haveAllAddOn"
                />
                <br />
                <FormattedMessage
                  id="MyAccount.haveAllAddOn2"
                />
              </p>

              <Button
                text={translate(intl, 'MyAccount.menu.goBack')}
                onClick={() => {
                  history.goBack();
                }}
              />
            </div>
          </div>
        </ModalBody>
      </Modal>
    </Container>);
}

ChangeAddOn.propTypes = {
  products: PropTypes.arrayOf,
  addOnList: PropTypes.array.isRequired,
  addOnsToShow: PropTypes.array,
  userPlan: PropTypes.object,
  history: PropTypes.object,
  intl: PropTypes.any,
  user: PropTypes.object,
  client: PropTypes.string,
  addOnSubscriptions: PropTypes.func,
  upgradeAddOnSubscriptions: PropTypes.func,
  downgradeAddOnSubscriptions: PropTypes.func,
  setAddOnsToShow: PropTypes.func,
  applyProductRules: PropTypes.func,
  addProduct: PropTypes.func,
  productsToBuy: PropTypes.object,
  subscriptions: PropTypes.array,
  methodId: PropTypes.string
};

export default ChangeAddOn;
